import {getBrowserPerformanceContext} from './browserPerformanceContext';
import type {recordRumDurationVital} from './datadogRum';
import {
  getInputActionElement,
  type InputActionContext,
  resolveInputAction,
} from './inputActionContext';
import {performanceObserverConfiguration} from './performanceObserverConfiguration';

const INPUT_DURATION_THRESHOLD_MS = 16;

/**
 * Reports click/keydown input-to-next-paint latency for the lifetime of RUM.
 * All durations are milliseconds:
 * - duration: total time from input creation through the next paint, including
 *   input delay, event processing, and presentation delay.
 * - inputDelayMs: time before event dispatch begins, often waiting for busy
 *   main-thread work to finish.
 * - processingDurationMs: elapsed time from the start to the end of event
 *   dispatch, including event handler execution.
 * - presentationDelayMs: time after dispatch until the next paint. This can
 *   include other work, rendering, and scheduling; it is not rendering CPU time.
 *   It is approximate because duration is rounded to 8 ms, so clamp negative
 *   remainders to zero. The phases may not sum exactly to the rounded total.
 *
 * startTime anchors the original input timestamp to the Unix epoch.
 * inputType identifies click or keydown; tagName identifies the closest matching
 * control. action is that control's data-perf-action, falling back to the
 * closest data-dd-action-name or aria-label; actionSource says which one.
 * These timings do not measure completion of asynchronous application work.
 */
export function observeRumInputLatency(
  report: typeof recordRumDurationVital
): (() => void) | undefined {
  if (
    typeof PerformanceObserver === 'undefined' ||
    !PerformanceObserver.supportedEntryTypes?.includes('event')
  ) {
    return undefined;
  }

  let observer: PerformanceObserver | undefined;
  let stopped = false;
  // Only initial setup can replay buffered entries. Later enables exclude input
  // created while disabled, even if the browser delivers it after observation starts.
  const startObserver = (buffered: boolean) => {
    const startedAt = buffered ? -Infinity : performance.now();
    const currentObserver = new PerformanceObserver(list => {
      // A disconnected observer may still have a queued callback after a config change.
      if (stopped || observer !== currentObserver) {
        return;
      }
      for (const entry of list.getEntries() as PerformanceEventTiming[]) {
        if (
          (entry.name !== 'click' && entry.name !== 'keydown') ||
          entry.duration < INPUT_DURATION_THRESHOLD_MS ||
          entry.startTime < startedAt
        ) {
          continue;
        }

        const target = entry.target;
        const actionElement = getInputActionElement(target);
        const inputContext = {
          inputType: entry.name,
          ...resolveInputAction(target),
        } satisfies InputActionContext;

        report('input_latency', {
          startTime: performance.timeOrigin + entry.startTime,
          duration: entry.duration,
          context: {
            // Event Timing arrives asynchronously, sometimes from the browser's
            // buffer. Heap values describe delivery time, not the original input
            // timestamp, and cannot reconstruct memory pressure during the input.
            ...getBrowserPerformanceContext(),
            ...inputContext,
            inputDelayMs: entry.processingStart - entry.startTime,
            processingDurationMs: entry.processingEnd - entry.processingStart,
            tagName: actionElement?.tagName,
            target,
            presentationDelayMs: Math.max(
              0,
              entry.startTime + entry.duration - entry.processingEnd
            ),
          },
        });
      }
    });

    const options: PerformanceObserverInit & {durationThreshold: number} = {
      type: 'event',
      buffered,
      durationThreshold: INPUT_DURATION_THRESHOLD_MS,
    };
    observer = currentObserver;
    observer.observe(options);
  };
  if (performanceObserverConfiguration.getState().instrumentInputLag) {
    startObserver(true);
  }
  const unsubscribe = performanceObserverConfiguration.subscribe(
    (config, previous) => {
      if (config.instrumentInputLag === previous.instrumentInputLag) {
        return;
      }
      observer?.disconnect();
      observer = undefined;
      if (config.instrumentInputLag) {
        // Re-enabling must not replay buffered events from disabled periods.
        startObserver(false);
      }
    }
  );
  return () => {
    if (stopped) {
      return;
    }
    stopped = true;
    unsubscribe();
    observer?.disconnect();
    observer = undefined;
  };
}
